import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView, arePricesLoading, getCheckoutDate, getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { checkCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getPackageOptionsByRoom, getCombinedRates, RateView } from 'components/OfferPage/Property/singlePropertyUtils'
import usePropertyBedbankViews from 'hooks/OfferPage/usePropertyBedbankViews'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useOffer from 'hooks/Offers/useOffer'
import useQueryParams from 'hooks/useQueryParams'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { initialRooms } from 'lib/offer/occupancyUtils'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import _BaseHotelFlightsToggleButton from './_BaseHotelFlightsToggleButton'

interface Props {
  offer: App.HotelOffer;
  rooms?: Array<App.Occupants>;
  checkIn?: string;
  checkOut?: string;
  duration?: number;
  previewMode: boolean;
  flightOrigin?: App.Airport;
  packageOptions: Array<OfferPackageOptionView>;
  canRedeemLuxPlusBenefits: boolean;
  pricesLoading: boolean;
}

function SinglePropertyHotelFlightsToggleButton(props: Props) {
  const {
    offer,
    rooms,
    checkIn,
    checkOut,
    duration,
    previewMode,
    flightOrigin,
    packageOptions,
    canRedeemLuxPlusBenefits,
    pricesLoading,
  } = props

  const utmContent = useQueryParams().get('utm_content')

  const [bedbankPackageViews] = usePropertyBedbankViews(offer.property.id)
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankOfferId)

  const bedbankFlightPrice = useBedbankFlightPrice(bedbankOffer, {
    rooms,
    startDate: checkIn,
    endDate: checkOut,
    originAirportCode: flightOrigin?.code,
  })

  const packageOptionsByRoom = useMemo(() => getPackageOptionsByRoom(packageOptions, bedbankPackageViews, mapping), [packageOptions, bedbankPackageViews, mapping])

  const combinedOptions = useMemo(() => packageOptionsByRoom.flatMap(room => room.lpc), [packageOptionsByRoom])

  const options = useMemo(() => {
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return combinedOptions.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, combinedOptions, pricesLoading])

  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
    },
  )

  const cheapestRoomRates = useMemo(() => packageOptionsByRoom.map(room => {
    const combinedRates = getCombinedRates(
      room.lpc,
      room.bedbank,
      availableOptions,
      {
        previewMode,
        utmContent,
      },
    )

    // Find cheapest rate for this room
    return combinedRates.filter(rate => rate.price > 0).reduce((cheapest: RateView, rate: RateView) => {
      if (!cheapest) return rate
      if (rate.type === 'LPC') {
        if ((rate.lpcRate?.hotelPrice ?? rate.price) < cheapest.price) return rate
      } else {
        if ((rate.bedbankRate?.rate.totals.total ?? rate.price) < cheapest.price) return rate
      }
      return cheapest
    }, null)
  }), [packageOptionsByRoom, previewMode, utmContent, availableOptions])

  const { hotelPrice, hotelMemberPrice, hotelFlightPrice } = useMemo(() => {
    const cheapestRate = cheapestRoomRates.filter(rate => !!rate).sort((a, b) => a.price - b.price)[0]

    if (!cheapestRate) return { hotelPrice: 0, hotelMemberPrice: 0, hotelFlightPrice: 0 }

    if (cheapestRate.type === 'bedbank') {
      const hotelPrice = cheapestRate.bedbankRate?.rate.totals.total ?? cheapestRate.price
      const hotelMemberPrice = cheapestRate.bedbankRate?.rate.totals.memberTotal ?? 0

      return {
        hotelPrice,
        hotelMemberPrice,
        hotelFlightPrice: canRedeemLuxPlusBenefits && hotelMemberPrice > 0 ? hotelMemberPrice + (bedbankFlightPrice ?? 0) : hotelPrice + (bedbankFlightPrice ?? 0),
      }
    }

    const hotelPrice = cheapestRate.lpcRate?.hotelPrice ?? cheapestRate.price
    const hotelMemberPrice = cheapestRate.lpcRate?.hotelMemberPrice ?? 0
    const flightPrice = cheapestRate.lpcRate?.flightPrice ?? 0

    return {
      hotelPrice,
      hotelMemberPrice,
      hotelFlightPrice: canRedeemLuxPlusBenefits && hotelMemberPrice > 0 ? hotelMemberPrice + flightPrice : hotelPrice + flightPrice,
    }
  }, [cheapestRoomRates, bedbankFlightPrice, canRedeemLuxPlusBenefits])

  const showMemberPrice = canRedeemLuxPlusBenefits && hotelMemberPrice > 0
  const hotelTotalPrice = useMemo(() => showMemberPrice ? hotelMemberPrice : hotelPrice, [showMemberPrice, hotelMemberPrice, hotelPrice])

  const flightsAvailable = offer.offerFlightsEnabled && !offer.flightCacheDisabled

  return <_BaseHotelFlightsToggleButton
    flightsAvailable={flightsAvailable}
    isMemberPrice={showMemberPrice}
    hotelTotalPrice={hotelTotalPrice}
    hotelFlightTotalPrice={hotelTotalPrice !== hotelFlightPrice ? hotelFlightPrice : undefined}
    pricesLoading={pricesLoading}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  previewMode: appState.system.previewMode,
  flightOrigin: state.airport,
  packageOptions: getDurationPackageOptions(state, appState),
  canRedeemLuxPlusBenefits: checkCanRedeemLuxPlusBenefits(appState),
  pricesLoading: arePricesLoading(appState, state),
}))(SinglePropertyHotelFlightsToggleButton)
